/**
 * Advance widths (per 1000 units of em) for the two PDF base-14 fonts the
 * certificate uses. The base-14 fonts are resolved by the PDF viewer itself,
 * so nothing is embedded in the file — but centring and wrapping still need
 * the metrics here, since PDF has no notion of text alignment.
 *
 * Indexed by `codePoint - 32`, covering WinAnsi/Latin-1 (U+0020..U+00FF).
 * A 0 entry means the font has no glyph for that code point.
 *
 * Extracted from the canonical Adobe metrics (verified against
 * /System/Library/Fonts/Helvetica.ttc at 1000 upm); do not hand-edit.
 */

const FIRST_CODE_POINT = 32;
const LAST_CODE_POINT = 255;

/** Width used for any code point outside the table — Helvetica's 'n'. */
const FALLBACK_WIDTH = 556;

const HELVETICA_WIDTHS: readonly number[] = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
  556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  278, 333, 556, 556, 556, 556, 260, 556, 333, 737, 370, 556, 584, 333, 737, 333,
  400, 549, 333, 333, 333, 576, 537, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  667, 667, 667, 667, 667, 667, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278,
  722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  556, 556, 556, 556, 556, 556, 889, 500, 556, 556, 556, 556, 278, 278, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 549, 611, 556, 556, 556, 556, 500, 556, 500,
];

const HELVETICA_BOLD_WIDTHS: readonly number[] = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
  975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
  333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
  611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  278, 333, 556, 556, 556, 556, 280, 556, 333, 737, 370, 556, 584, 333, 737, 333,
  400, 549, 333, 333, 333, 576, 556, 278, 333, 333, 365, 556, 834, 834, 834, 611,
  722, 722, 722, 722, 722, 722, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278,
  722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611,
  556, 556, 556, 556, 556, 556, 889, 556, 556, 556, 556, 556, 278, 278, 278, 278,
  611, 611, 611, 611, 611, 611, 611, 549, 611, 611, 611, 611, 611, 556, 611, 556,
];

export type PdfFont = 'Helvetica' | 'Helvetica-Bold';

const WIDTHS_BY_FONT: Record<PdfFont, readonly number[]> = {
  Helvetica: HELVETICA_WIDTHS,
  'Helvetica-Bold': HELVETICA_BOLD_WIDTHS,
};

/**
 * Width of `text` in points when set in `font` at `size`, including the
 * extra tracking applied between characters (PDF's Tc operator, which adds
 * after every glyph — including the last, which we subtract back off).
 */
export function measureText(
  text: string,
  font: PdfFont,
  size: number,
  charSpacing = 0,
): number {
  const widths = WIDTHS_BY_FONT[font];
  let total = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    const width =
      code >= FIRST_CODE_POINT && code <= LAST_CODE_POINT
        ? widths[code - FIRST_CODE_POINT] || FALLBACK_WIDTH
        : FALLBACK_WIDTH;
    total += (width / 1000) * size + charSpacing;
  }
  return total > 0 ? total - charSpacing : 0;
}
